import { computed, type ComputedRef, reactive, type Ref, ref, watch } from 'vue'

import {
  maklerFormPrefillFromSession,
  maklerQuickscrapeFromSession,
  type OnboardingSessionPayload,
  scrapeStatusFromSession,
} from '@/lib/ai/assistantApi'
import { valuePlausibleForFormField } from '@/lib/ai/onboardingChat'
import { hasMeaningfulOnboardingValue } from '@/lib/ai/onboardingFieldValues'
import { availableCountryCode, resolveAvailableCountry } from '@/lib/onboardingCountry'
import { isUsOnboardingMarket } from '@/lib/onboardingMarket'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { PendingAction } from '@/stores/aiAssistant/types'

import {
  applyConfirmedAiValueToForm,
  formKeysAffectedByAiAction,
} from '../lib/onboardingFormMapping'
import {
  normalizeOnboardingPhone,
  ONBOARDING_PHONE_FIELDS,
  resolveOnboardingPhoneCountry,
} from '../lib/onboardingPhoneValidation'
import {
  applyPrefillToForm,
  applyQuickscrapeToForm,
  type ChatBillingPrefill,
  chatBillingPrefillFromMaklerPrefill,
  companyRecordValues,
  createEmptyOnboardingFormData,
  matchesSavedCompanyValue,
  normalizeQuickscrapePayload,
  type OnboardingFormData,
  type QuickscrapePayload,
} from '../lib/onboardingPrefill'
import { useBrokerageSameAsName } from './useBrokerageSameAsName'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>
type Translate = (key: string, ...args: unknown[]) => string

export function useOnboardingForm(options: {
  company: ComputedRef<CompanyData | null>
  wizardStep: Ref<number>
  t: Translate
  authEmail: ComputedRef<string>
  companyIdFallback: ComputedRef<string>
  syncAiContext: Ref<() => void>
}) {
  const { company, wizardStep, t, authEmail, companyIdFallback, syncAiContext } = options
  const formData = reactive(createEmptyOnboardingFormData()) as OnboardingFormData
  const userEditedFields = reactive(new Set<string>())
  const aiCapturedFields = reactive(new Set<string>())
  const chatBillingPrefill = ref<ChatBillingPrefill | null>(null)
  const pendingGenaisisPrefill = ref<Record<string, unknown> | null>(null)
  const pendingQuickscrape = ref<QuickscrapePayload | null>(null)
  const genaisisScrapeCompleteNotified = ref(false)
  const sessionMarket = ref<string | null>(null)

  const countryCodeToFormDisplay = (raw: unknown) => {
    const row = resolveAvailableCountry(raw, t)
    return row ? t(row.text) : ''
  }
  const countryDisplayFromQuickscrape = (scrape: {
    countryCode?: unknown
    companyCountryName?: unknown
  }) =>
    countryCodeToFormDisplay(scrape?.countryCode) ||
    countryCodeToFormDisplay(scrape?.companyCountryName)
  const countryValueForSubmit = () => availableCountryCode(formData.country, t)

  const isUsOnboarding = computed(() =>
    isUsOnboardingMarket({
      companyCountry: company.value?.country,
      addressCountry: company.value?.address?.country,
      variant: company.value?.variant,
      sessionMarket: sessionMarket.value,
      formCountryCode: availableCountryCode(formData.country, t),
    })
  )

  /** ISO country used for national-format phone numbers (`+1` for US). */
  const defaultPhoneCountry = computed(() =>
    resolveOnboardingPhoneCountry(isUsOnboarding.value, formData.country, t)
  )

  // Prefills (company record, scrape, AI) can carry national numbers without a
  // calling code; store them as E.164 so PhoneField and validation agree.
  watch(
    [() => ONBOARDING_PHONE_FIELDS.map((key) => formData[key]), defaultPhoneCountry],
    () => {
      for (const key of ONBOARDING_PHONE_FIELDS) {
        const normalized = normalizeOnboardingPhone(formData[key], defaultPhoneCountry.value)
        if (normalized !== formData[key]) formData[key] = normalized as string
      }
    },
    { immediate: true }
  )

  function handleFieldUpdate(field: string, value: string | boolean) {
    ;(formData as Record<string, unknown>)[field] = value
    userEditedFields.add(field)
  }

  // "brokerageName" sits in the same autosave group as "name", so the name's autosave persists it.
  useBrokerageSameAsName({ formData, setField: handleFieldUpdate })

  function populateFormFromCompany(c: CompanyData) {
    const setIfUntouched = (field: string, value: unknown) => {
      if (userEditedFields.has(field) || aiCapturedFields.has(field)) return
      if (value == null || value === '') return
      if (!hasMeaningfulOnboardingValue(field, value)) return
      ;(formData as Record<string, unknown>)[field] = value
    }
    setIfUntouched('name', c.name)
    setIfUntouched('website', c.links?.website)
    setIfUntouched('slogan', c.slogan)
    setIfUntouched('primaryColor', c.theme?.colors?.primary)
    setIfUntouched('secondaryColor', c.theme?.colors?.secondary)
    const addr = c.address || {}
    setIfUntouched('street', addr.street)
    setIfUntouched('city', addr.city)
    setIfUntouched('zipcode', addr.zip)
    setIfUntouched('country', countryCodeToFormDisplay(addr.country))
    setIfUntouched('state', addr.state)
    setIfUntouched('brokerageName', c.brokerageName)
    setIfUntouched('licenseNumber', c.licenseNumber)
    setIfUntouched('licenseStatus', c.licenseStatus)
    if (c.brokerageNameSameAsName != null) {
      setIfUntouched('brokerageNameSameAsName', Boolean(c.brokerageNameSameAsName))
    }
    setIfUntouched('commission', c.commission)
    setIfUntouched('businessDescription', c.meta?.businessDescription)
    const amb = c.ambassador || {}
    setIfUntouched('ambassadorSalutation', amb.salutation)
    setIfUntouched('ambassadorFirstName', amb.firstname)
    setIfUntouched('ambassadorLastName', amb.lastname)
    setIfUntouched('phone', amb.phone)
    setIfUntouched('ambassadorMobile', amb.mobile)
    const general =
      (c.contactPersons || []).find((p: { type?: string }) => p.type === 'GENERAL') || {}
    setIfUntouched('contactFirstName', general.firstname)
    setIfUntouched('contactLastName', general.lastname)
    setIfUntouched('contactPhone', general.phone)
    setIfUntouched('contactEmail', general.email)
    setIfUntouched('linkImprint', c.links?.imprint)
    setIfUntouched('linkPrivacy', c.links?.privacy)
    setIfUntouched('linkAbout', c.links?.about)
    setIfUntouched('ambassadorRole', amb.role)
    setIfUntouched('ambassadorEmail', amb.email || authEmail.value)
    // Company File URLs are not in genaisis prefill. Copy them onto formData when
    // present, but never clear a URL already stored from an upload.
    const setImageIfPresent = (
      field: 'companyLogo' | 'teamPicture' | 'portraitImage',
      value: unknown
    ) => {
      if (userEditedFields.has(field) || aiCapturedFields.has(field)) return
      if (value == null || value === '') return
      if (!hasMeaningfulOnboardingValue(field, value)) return
      formData[field] = String(value)
    }
    setImageIfPresent('companyLogo', c?.images?.logo?.url)
    setImageIfPresent('teamPicture', c?.images?.team?.url)
    setImageIfPresent('portraitImage', c?.ambassador?.image?.url)
    if (c.automaticIntegration != null) {
      setIfUntouched('automaticIntegration', Boolean(c.automaticIntegration))
    }
  }

  function applyChatFieldUpdate({ field, value }: { field: string; value: unknown }) {
    if (wizardStep.value < 2) return
    if (!Object.prototype.hasOwnProperty.call(formData, field)) return
    if (field === 'automaticIntegration') {
      formData.automaticIntegration = Boolean(value)
      aiCapturedFields.add(field)
      useAiAssistantStore().markFieldUpdatedByAi(field)
      syncAiContext.value()
      return
    }
    if (typeof value !== 'string' || !valuePlausibleForFormField(field, value)) return
    // Echo of the user's own input (e.g. the image_selection sync after a manual
    // upload) — keep it a user value, not "Captured automatically".
    if (
      userEditedFields.has(field) &&
      String((formData as Record<string, unknown>)[field] ?? '').trim() === value.trim()
    ) {
      syncAiContext.value()
      return
    }
    // Same for a chat update that repeats the saved company value (brokerage name,
    // license number, state) — consistent with the prefill path.
    if (
      !aiCapturedFields.has(field) &&
      matchesSavedCompanyValue(field, value, companyRecordValues(company.value))
    ) {
      ;(formData as Record<string, unknown>)[field] = value.trim()
      syncAiContext.value()
      return
    }
    if (field === 'country') {
      const display = countryCodeToFormDisplay(value)
      if (!display) return
      formData.country = display
    } else {
      ;(formData as Record<string, unknown>)[field] = String(value).trim()
    }
    aiCapturedFields.add(field)
    useAiAssistantStore().markFieldUpdatedByAi(field)
    syncAiContext.value()
  }

  function applyPrefill(prefill: Record<string, unknown>) {
    applyPrefillToForm(
      formData,
      prefill,
      userEditedFields,
      aiCapturedFields,
      (code) => countryDisplayFromQuickscrape({ countryCode: code }),
      companyRecordValues(company.value)
    )
    syncAiContext.value()
  }

  function applyQuickscrape(scrape: QuickscrapePayload) {
    applyQuickscrapeToForm(
      formData,
      scrape,
      userEditedFields,
      aiCapturedFields,
      countryDisplayFromQuickscrape
    )
    syncAiContext.value()
  }

  function flushPendingGenaisisPrefill() {
    if (wizardStep.value < 2) return
    const pending = pendingGenaisisPrefill.value
    const pendingQs = pendingQuickscrape.value
    if (!pending && !pendingQs) return
    pendingGenaisisPrefill.value = null
    pendingQuickscrape.value = null
    if (pending) applyPrefill(pending)
    if (pendingQs) applyQuickscrape(pendingQs)
  }

  function applyGenaisisSessionToForm(session: OnboardingSessionPayload | null | undefined) {
    if (!session) return
    const market = session.market ?? session.session_market
    if (typeof market === 'string' && market.trim()) sessionMarket.value = market.trim()
    const prefill = maklerFormPrefillFromSession(session) as Record<string, unknown> | null
    const quickscrapeRaw = maklerQuickscrapeFromSession(session)
    const quickscrape = quickscrapeRaw ? normalizeQuickscrapePayload(quickscrapeRaw) : null
    if (prefill) {
      chatBillingPrefill.value = chatBillingPrefillFromMaklerPrefill(prefill)
    }
    if (wizardStep.value >= 2) {
      pendingGenaisisPrefill.value = null
      pendingQuickscrape.value = null
      if (prefill) applyPrefill(prefill)
      if (quickscrape) applyQuickscrape(quickscrape)
      if (import.meta.env.DEV && prefill) {
        console.warn('[Onboarding] applied Genaisis prefill keys:', Object.keys(prefill))
      }
    } else {
      pendingGenaisisPrefill.value = prefill
      pendingQuickscrape.value = quickscrape
    }
    const status = scrapeStatusFromSession(session)
    if (status === 'completed' && !genaisisScrapeCompleteNotified.value) {
      genaisisScrapeCompleteNotified.value = true
      const aiStore = useAiAssistantStore()
      aiStore.open()
      aiStore.setChatAfterScrapeFinished(session)
      return session
    }
    return null
  }

  function syncFormAfterAiConfirmedAction(action: PendingAction) {
    const keys = formKeysAffectedByAiAction(action, formData as unknown as Record<string, unknown>)
    for (const key of keys) userEditedFields.delete(key)
    if (company.value) populateFormFromCompany(company.value)
    applyConfirmedAiValueToForm(
      action,
      keys,
      formData as unknown as Record<string, unknown>,
      countryCodeToFormDisplay
    )
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(formData, key)) aiCapturedFields.add(key)
    }
    const aiStore = useAiAssistantStore()
    for (const key of keys) aiStore.markFieldUpdatedByAi(key)
    syncAiContext.value()
  }

  function getAmbassadorFormDraftForAi() {
    if (!company.value) return null
    return {
      salutation: (formData.ambassadorSalutation || '').trim(),
      firstname: (formData.ambassadorFirstName || '').trim(),
      lastname: (formData.ambassadorLastName || '').trim(),
      role: (formData.ambassadorRole || '').trim(),
      email: (formData.ambassadorEmail || '').trim(),
      phone: (formData.phone || '').trim(),
      mobile: (formData.ambassadorMobile || '').trim(),
    }
  }

  /** An image the user uploaded themselves — a user value, never "Captured automatically". */
  function applyImageField(field: 'companyLogo' | 'teamPicture' | 'portraitImage', url: string) {
    const trimmed = String(url ?? '').trim()
    if (!trimmed) return
    formData[field] = trimmed
    userEditedFields.add(field)
    aiCapturedFields.delete(field)
    syncAiContext.value()
  }

  function firstImageUrl(...candidates: unknown[]): string {
    for (const candidate of candidates) {
      const value = typeof candidate === 'string' ? candidate.trim() : ''
      if (value) return value
    }
    return ''
  }

  function enterDataContextValues(imageOverrides: Record<string, string> = {}) {
    const c = company.value
    return {
      companyId: c?.id ?? companyIdFallback.value ?? '',
      licenseAgreementAccepted: Boolean(c?.licenseAgreement?.acceptedAt),
      name: formData.name,
      website: formData.website,
      street: formData.street,
      city: formData.city,
      zipcode: formData.zipcode,
      country: formData.country,
      state: formData.state,
      brokerageName: formData.brokerageName,
      licenseNumber: formData.licenseNumber,
      licenseStatus: formData.licenseStatus,
      ambassadorSalutation: formData.ambassadorSalutation,
      ambassadorFirstName: formData.ambassadorFirstName,
      ambassadorLastName: formData.ambassadorLastName,
      phone: formData.phone,
      ambassadorMobile: formData.ambassadorMobile,
      ambassadorRole: formData.ambassadorRole,
      ambassadorEmail: formData.ambassadorEmail,
      contactFirstName: formData.contactFirstName,
      contactLastName: formData.contactLastName,
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
      commission: formData.commission,
      slogan: formData.slogan,
      businessDescription: formData.businessDescription,
      googleReviewLink: formData.googleReviewLink,
      primaryColor: formData.primaryColor,
      secondaryColor: formData.secondaryColor,
      linkImprint: formData.linkImprint,
      linkPrivacy: formData.linkPrivacy,
      linkAbout: formData.linkAbout,
      companyLogo: firstImageUrl(
        imageOverrides.companyLogo,
        formData.companyLogo,
        c?.images?.logo?.url
      ),
      teamPicture: firstImageUrl(
        imageOverrides.teamPicture,
        formData.teamPicture,
        c?.images?.team?.url
      ),
      portraitImage: firstImageUrl(
        imageOverrides.portraitImage,
        formData.portraitImage,
        c?.ambassador?.image?.url
      ),
    }
  }

  const billingPrefillContact = computed(() => {
    const chat = chatBillingPrefill.value || {}
    const pick = (
      chatKey: keyof ChatBillingPrefill,
      formKey: keyof OnboardingFormData,
      fallback = ''
    ) => {
      const fromChat = (chat[chatKey] || '').toString().trim()
      if (fromChat) return fromChat
      const fromForm = (formData[formKey] || '').toString().trim()
      if (fromForm) return fromForm
      return (fallback || '').toString().trim()
    }
    return {
      firstname: pick('billingFirstname', 'ambassadorFirstName'),
      lastname: pick('billingLastname', 'ambassadorLastName'),
      email: pick('billingEmail', 'ambassadorEmail'),
      phone: pick('billingPhone', 'phone'),
      companyName: pick('billingCompanyName', 'name', company.value?.name || ''),
      vatId: (chat.vatId || '').toString().trim(),
      taxNumber: (chat.taxNumber || '').toString().trim(),
      billingNoVat: chat.billingNoVat === true || chat.billingNoVat === 'true',
    }
  })

  const billingDataProps = computed(() => {
    if (!company.value) return null
    const { id: companyId, country, billing, customerNumber, address, name } = company.value
    const chat = chatBillingPrefill.value || {}
    const mergedBilling = {
      ...(billing || {}),
      address: billing?.address || {},
      contact: billing?.contact || {},
    }
    if (!mergedBilling.vatId && chat.vatId) mergedBilling.vatId = chat.vatId
    if (!mergedBilling.taxNumber && chat.taxNumber) mergedBilling.taxNumber = chat.taxNumber
    if (chat.billingNoVat === true || chat.billingNoVat === 'true') mergedBilling.chatNoVat = true
    return { companyId, country, address, name, customerNumber, billing: mergedBilling }
  })

  const landingpageDataProps = computed(() => {
    if (!company.value) return null
    const { id: companyId, variant, country, bookedLandingpageSlugs } = company.value
    return { companyId, variant, country, bookedLandingpageSlugs: bookedLandingpageSlugs || [] }
  })

  return {
    formData,
    userEditedFields,
    aiCapturedFields,
    chatBillingPrefill,
    genaisisScrapeCompleteNotified,
    countryCodeToFormDisplay,
    countryValueForSubmit,
    isUsOnboarding,
    handleFieldUpdate,
    populateFormFromCompany,
    applyChatFieldUpdate,
    flushPendingGenaisisPrefill,
    applyGenaisisSessionToForm,
    syncFormAfterAiConfirmedAction,
    getAmbassadorFormDraftForAi,
    applyImageField,
    enterDataContextValues,
    billingPrefillContact,
    billingDataProps,
    landingpageDataProps,
  }
}
